import { Link, useParams, useSearchParams, useNavigate, useOutletContext } from "react-router-dom"
import { useComputed, useSignal } from "@preact/signals-react"

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Spinner from 'react-bootstrap/Spinner'
import Stack from 'react-bootstrap/Stack'

import ConfirmationModal from "../../components/ConfirmationModal"
import DebugAccordion from "../../components/DebugAccordion"
import JsonForm from "../../components/JsonForm.jsx"
import PageTitle from "../../components/PageTitle.jsx"
import ProfileNav from "./components/ProfileNav"
import ProfileTools from "./components/ProfileTools.jsx";
import { addNotification, NotificationTypes } from "../../stores/notification-store.js";
import { checkApiInterfaceConflicts, isEmptyObject, makeFieldsReadonly } from '../../utils/utils.js'
import { UpdateProfile } from "../../utils/api.js";
import { NOT_FOUND } from "../../utils/constants.js"

import useProfile from "../../hooks/useProfile.js"
import useProfileSchema, { profileSchema } from "../../hooks/useProfileSchema.js"
import useSchemaManifest from "../../hooks/useSchemaManifest.js"

export default function ProfileApiInterfaceDetail() {

    const navigate = useNavigate();
    const { beId, profileId, id } = useParams()
    const [searchParams] = useSearchParams()
    const port = searchParams.get('port')
    const editMode = !!port
    const debug = !!searchParams.get('debug')
    const { config, accessToken, canEditSignal, accordionData: sidebarAccordionData  } = useOutletContext()
    const { manifest } = useSchemaManifest()

    const { profile, loading: profileLoading, isLoaded: profileIsLoaded, modified, ccschema } = useProfile(beId, profileId, config, accessToken)
    const apiInterface = useComputed(() => {
        if (profile.value.componentProfile && editMode) {
            const index = profile.value.componentProfile.config.apiInterfaceConfig.apiInterface.findIndex(a => a.listenPort == port)
            const currentApiInterface = profile.value.componentProfile.config.apiInterfaceConfig.apiInterface[index]
            const jsonFormApiInterface = { config: currentApiInterface }
            return jsonFormApiInterface
        }
        return null
    })
    const apiInterfaceIndex = useComputed(() => {
        if (profile.value.componentProfile && editMode) {
            // selected apiInterface index. This is used when editing an interface since there are no unique id's
            const index = profile.value.componentProfile.config.apiInterfaceConfig.apiInterface.findIndex(a => a.listenPort == port)
            return index
        }
        return -1
    })

    const { loading: schemaLoading, isLoaded: schemaIsLoaded, version: profileVersion } = useProfileSchema(beId, ccschema.value, manifest.value, config, accessToken)
    const apiInterfaceSchema = useComputed(() => {
        if (profileSchema.value.properties) {
            // extract the selected api interface schema from the component config schema
            const jsonFormConfigSchema = { config: profileSchema.value?.properties?.config?.properties?.apiInterfaceConfig?.properties?.apiInterface?.items?.find(a => a.properties.cdType["s4-model-key"] == parseInt(id, 10)) }
            return jsonFormConfigSchema
        }
        return {}
    })
    const pageTitle = useComputed(() => {
        const currentCaption = apiInterfaceSchema.value?.config?.properties?.cdType['s4-caption'] || ''
        return editMode ?
        `Manage ${currentCaption} UTG API Interface` :
        `Add ${currentCaption} UTG API Interface`
    })

    const jsonFormData = useComputed(() => {

        if (isEmptyObject(profileSchema.value) || isEmptyObject(profile.value)) {
            return
        }
        
        // deep clone the schema
        let clonedSchema = structuredClone(apiInterfaceSchema.value)

        // disable editing of all fields if canEditSignal is false
        if (!canEditSignal.value) {
            clonedSchema = makeFieldsReadonly(clonedSchema)
        }
        
        // sets the variable on the global windows object so that shift-utils.js code can determine if we are working with a profile or config schema
        globalThis.SCHEMA_MODE = 'PROFILE'
        console.log('Set SCHEMA_MODE to', globalThis.SCHEMA_MODE)

        const formData = {
            schema: clonedSchema,
            loadedComponentConfig: profile.value.componentProfile.config,
            onSubmitValid: handleFormSubmit,
            onCancel: handleCancel,
            onFormChange: handleFormChange,
            canEditConfig: canEditSignal.value
        }
        if (apiInterface.value)
        {
            formData.value = apiInterface.value
        }
        console.log('formData from ProfileApiInterfaceDetail', formData)
        return formData
    }) 

    const showCancelModal = useSignal(false)
    const isFormEdited = useSignal(false)
    const isFormSubmitting = useSignal(false)
    const accordionData = [
        { name: 'API Interface Data', data: apiInterface },
        { name: 'API Interface Schema', data: apiInterfaceSchema },
        { name: 'API Interface Index', data: apiInterfaceIndex },
        { name: 'Component Profile', data: profile },
        { name: 'Profile Schema', data: profileSchema },
        { name: 'Access Token', data: accessToken},
        { name: 'App Config', data: config},
        { name: 'Schema Manifest', data: manifest }
    ]

    const handleFormSubmit = values => {
        // short-circuit if the handler was already called. This blocks clicking the submit a second time while already processing a previous submit
        if (isFormSubmitting.value) {
            return false
        }
        isFormSubmitting.value = true
        try {
            console.log('values', values)
            if (values) {
                
                // build the full config data including apiInterface and deviceConfig data to save back to server
                let data = {
                    id: profile.value.componentProfile.id,
                    name: profile.value.componentProfile.name,
                    businessEntityId: profile.value.componentProfile.businessEntityId,
                    config: profile.value.componentProfile.config,
                }

                // make sure the apiInterfaceConfig is present before adding to it
                data.config.apiInterfaceConfig = data.config.apiInterfaceConfig || {}
                data.config.apiInterfaceConfig.apiInterface = data.config.apiInterfaceConfig.apiInterface || []

                if (editMode) {
                    // replace the existing selected apiInterface with data from values 
                    data.config.apiInterfaceConfig.apiInterface[apiInterfaceIndex.value] = values.config
                }
                else {
                    // insert values into the apiInterface array
                    data.config.apiInterfaceConfig.apiInterface.push(values.config)
                }

                // validate listenPort uniqueness in case jsonform validation fails
                if (checkApiInterfaceConflicts(data.config.apiInterfaceConfig.apiInterface)) {
                    addNotification("Unable to save due to conflict. All API Interfaces must have a unique Port.", NotificationTypes.ERROR)
                    return false
                }
                
                // add a schema value to the config data. This allows us to load this specific schema rather than a default one in future edits
                data.config.utgGlobal.ccschema = profileVersion.value

                console.log(data)
                UpdateProfile(beId, profileId, data, config, accessToken)
                    .then(result => {
                        modified.value = true
                        addNotification('Api Interface was successfully updated.', NotificationTypes.SUCCESS, true)
                        navigate('../')
                    })
                    .catch(error => {
                        console.log(error.toString())
                        addNotification("Unable to update the Api Interface.")
                        return false
                    })
                    .finally(() => {
                        isFormSubmitting.value = false
                        // must return false to avoid postback
                        return false
                    })
            }
        }
        catch (error) {
            console.log(error)
        }

        // return false to prevent form submit from refreshing the page
        return false
    }

    const handleCancel = () => {
        if (isFormEdited.value) {
            showCancelModal.value = true
        }
        else {
            navigate('../apiinterfaces')
        }
    }

    const handleFormChange = () => {
        isFormEdited.value = true
    }

    if (!profileIsLoaded.value || !schemaIsLoaded.value) {
        return (
            <div className="d-flex justify-content-center">
                <Spinner className="mt-5 align-center" animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        )
    }

    return (
        <>
        <ConfirmationModal show={showCancelModal.value} handleCancel={() => showCancelModal.value = false} handleConfirm={() => navigate('../apiinterfaces')} message={'You have unsaved changes to this form. Are you sure you want to leave and lose these changes?'} title={"Warning: Unsaved Changes"} cancelText="Continue Editing" confirmText="Lose Changes" />
        <Breadcrumb className="px-3 pt-1 bg-light">
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/profiles` }}>
                Profile List
            </Breadcrumb.Item>
            <Breadcrumb.Item active>Manage {profile.value.componentProfile.name}</Breadcrumb.Item>
        </Breadcrumb>
        <Stack direction="horizontal" gap={3}>
            <PageTitle title={pageTitle.value} canEditSignal={canEditSignal} />
            <ProfileTools profile={profile.value}></ProfileTools>
        </Stack>
        <ProfileNav beId={beId} profileId={profile.value.componentProfile.id} activeKey="2" debug={debug}></ProfileNav>
        <JsonForm data={jsonFormData.value}></JsonForm>
        <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}